/**
 * Le contrat des `field_key` produits par le modèle — volet B de
 * `D-NEW-SEC-INJECTIONS-SQL-ET-PROMPT`.
 *
 * ## Ce qu'on ferme, et ce qu'on ne ferme délibérément PAS
 *
 * Un document d'assurance vient d'un tiers, et personne ne le relit avant qu'il
 * n'entre dans un prompt. Sa sortie n'est donc **pas** une source de confiance,
 * et rien ne doit atteindre l'espace canonique du seul fait que le modèle l'a
 * produit.
 *
 * Mais fermer l'espace par une **liste exhaustive de clés métier** casserait le
 * produit : mesuré sur les données réelles, le vocabulaire est ouvert et
 * instable — 111 couples (passe, clé) distincts, des clés inventées en français
 * (`prime_totale_mensuelle_ild`, `numero_offre`, `garanties_identifiees`) que
 * personne n'a déclarées. On ferme donc **la forme, le namespace et les cibles**,
 * et on rend le reste **visible** au lieu de le laisser passer ou de le jeter.
 *
 * ## Trois issues, et elles doivent rester distinguables
 *
 * - **canonique** — la clé appartient au contrat déclaré de la passe ;
 * - **quarantaine** — clé bien formée mais hors contrat : elle est **conservée
 *   et signalée**, jamais silencieusement acceptée comme canonique ;
 * - **refus** — clé malformée, cible interdite, ou suffixe réservé au serveur :
 *   elle n'est pas persistée du tout, et une anomalie le dit.
 *
 * **Aucun rejet silencieux.** Un champ jeté sans trace apprend au conseiller que
 * ce qu'il voit est complet, alors qu'il ne l'est pas.
 *
 * ## Pourquoi le contrat canonique est DÉRIVÉ ici, alors que les allowlists SQL
 * ## sont écrites à la main
 *
 * Ce n'est pas la même chose. Une allowlist SQL dérivée d'un schéma Zod suivrait
 * la forme d'un objet arrivé du réseau. Ici, `EXTRACTION_PASSES[x].fields` est
 * **ce que le serveur a demandé au modèle** — c'est la commande, pas la réponse.
 * La dériver est donc correct et s'entretient tout seul : ajouter un champ au
 * prompt le rend canonique, ce qui est exactement l'intention.
 */

import { EXTRACTION_PASSES } from "../prompts/extraction.js";
import {
  INTAKE_EXTRACTION_PASSES,
  INTAKE_FIELD_KEY_NAMESPACES,
} from "../prompts/intake-extraction.js";

/**
 * Les passes intake, dérivées de leur contrat — jamais recopiées. Une passe
 * intake ajoutée demain raisonnera par namespace sans qu'on y pense.
 */
const PASSES_INTAKE: ReadonlySet<string> = new Set(Object.keys(INTAKE_EXTRACTION_PASSES));

// -----------------------------------------------------------------------------
// La forme
// -----------------------------------------------------------------------------

/**
 * Minuscules, chiffres et soulignés, en 1 à 3 segments séparés par des points.
 *
 * Plus stricte que `INTAKE_FIELD_KEY_REGEX`, qui acceptait un souligné initial,
 * des points consécutifs et un point final — trois formes qu'aucune clé réelle
 * n'utilise et qui n'ont aucune raison d'exister. Les données mesurées ont une
 * profondeur maximale de 2 segments ; on en autorise 3, pas davantage.
 */
export const FORME_FIELD_KEY = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*){0,2}$/;

/** Au-delà, ce n'est plus une clé — c'est une charge utile. */
export const LONGUEUR_MAX_FIELD_KEY = 120;

// -----------------------------------------------------------------------------
// Ce que le modèle ne peut jamais nommer
// -----------------------------------------------------------------------------

/**
 * Suffixes que **seul le serveur** appose. `project.insurer_codes_raw` est une
 * annotation produite par la partition contre le registre : elle dit « le
 * serveur n'a pas su mapper ceci ». Si le modèle pouvait l'émettre lui-même, il
 * contreferait un constat du serveur.
 */
export const SUFFIXES_RESERVES_SERVEUR = ["_raw"] as const;

/**
 * Segments qui désignent une **identité ou une cible**, jamais une donnée
 * extraite. Un document ne choisit pas la ligne qu'on écrit.
 *
 * Vérifié contre les 111 couples (passe, clé) réellement présents : aucune clé
 * légitime n'en contient.
 */
export const SEGMENTS_INTERDITS: ReadonlySet<string> = new Set([
  "id",
  "tenant_id",
  "submission_id",
  "project_id",
  "extraction_id",
  "intake_event_id",
  "company_id",
  "contact_id",
  "user_id",
  "file_id",
  "broker_id",
  "owner_id",
  "created_at",
  "updated_at",
  "deleted_at",
]);

// -----------------------------------------------------------------------------
// Le contrat canonique, par passe
// -----------------------------------------------------------------------------

/**
 * La passe `identification` ne passe pas par `parsed.fields` mais par un
 * aplatissement du JSON top-level. Son contrat est donc cette liste, et non
 * `EXTRACTION_PASSES.identification.fields`.
 *
 * Écrite ici plutôt qu'importée de `extraction-service` : c'est un **contrat**,
 * et il doit vivre avec les autres, pas à côté du code qui l'applique.
 */
export const CHAMPS_IDENTIFICATION: readonly string[] = [
  "insurer_code",
  "insurer_name",
  "detection_confidence",
  "document_type",
  "language",
  "is_blank_form",
  "blank_form_ratio",
  "effect_date_hint",
  "effect_date",
  "uncertainty",
  "sections_present",
];

/** Les clés canoniques d'une passe, ou `null` si la passe est inconnue. */
export function champsCanoniquesDePasse(passId: string): ReadonlySet<string> | null {
  if (passId === "identification") return new Set(CHAMPS_IDENTIFICATION);
  const passe = (EXTRACTION_PASSES as Record<string, { fields: readonly string[] } | undefined>)[
    passId
  ];
  return passe ? new Set(passe.fields) : null;
}

// -----------------------------------------------------------------------------
// Le classement
// -----------------------------------------------------------------------------

export type IssueChamp = "canonique" | "quarantaine" | "refus";

export type RaisonRefus =
  | "forme_invalide"
  | "trop_longue"
  | "segment_interdit"
  | "suffixe_reserve_serveur"
  | "passe_inconnue";

export type ClassementChamp =
  | { issue: "canonique" }
  | { issue: "quarantaine" }
  | { issue: "refus"; raison: RaisonRefus };

/**
 * Classe une clé produite par le modèle, pour une passe donnée.
 *
 * **À appeler sur la sortie BRUTE du modèle**, avant toute annotation serveur :
 * c'est ce qui permet de refuser un `_raw` contrefait sans refuser celui que le
 * serveur apposera juste après.
 */
export function classerChampExtrait(fieldKey: string, passId: string): ClassementChamp {
  if (fieldKey.length > LONGUEUR_MAX_FIELD_KEY) {
    return { issue: "refus", raison: "trop_longue" };
  }
  if (!FORME_FIELD_KEY.test(fieldKey)) {
    return { issue: "refus", raison: "forme_invalide" };
  }
  if (SUFFIXES_RESERVES_SERVEUR.some((s) => fieldKey.endsWith(s))) {
    return { issue: "refus", raison: "suffixe_reserve_serveur" };
  }
  for (const segment of fieldKey.split(".")) {
    if (SEGMENTS_INTERDITS.has(segment)) {
      return { issue: "refus", raison: "segment_interdit" };
    }
  }

  // La passe intake raisonne par namespace : son vocabulaire métier est large
  // et déclaré par préfixe, pas par énumération.
  if (PASSES_INTAKE.has(passId)) {
    return INTAKE_FIELD_KEY_NAMESPACES.some((ns) => fieldKey.startsWith(ns))
      ? { issue: "canonique" }
      : { issue: "quarantaine" };
  }

  const canoniques = champsCanoniquesDePasse(passId);
  if (canoniques === null) {
    // Une passe qu'on ne connaît pas n'a pas de contrat : on ne peut rien
    // déclarer canonique en son nom.
    return { issue: "refus", raison: "passe_inconnue" };
  }
  return canoniques.has(fieldKey) ? { issue: "canonique" } : { issue: "quarantaine" };
}
